import * as React from 'react'

import {
  ChevronDownIcon,
  ChevronRightIcon,
  GripVerticalIcon,
} from 'lucide-react'
import {
  arrayMove,
  SortableContainer,
  SortableElement,
  SortableHandle,
} from 'react-sortable-hoc'

import Localization from '../../localization/LocalizationManager'
import { CollapseButton } from '../CollapseButton/CollapseButton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../shadcn-ui/tabs'
import { useResourceEdit } from './ResourceEditContext'

export interface IResourceEditSection {
  /** Stable id used for ordering and expand/collapse state. */
  id: string
  /** Collapsible section header label. */
  label: string
  content: React.ReactNode
  /** Whether the section starts expanded (default false). */
  defaultExpanded?: boolean
  /**
   * Keep the content mounted (hidden) while collapsed. Set on the section whose
   * content is the form so the footer Save keeps working when it's collapsed.
   */
  keepMounted?: boolean
  /** Hidden in creation mode (e.g. controls / map / telemetry need a saved record). */
  requiresSavedRecord?: boolean
}

interface IProps {
  sections: IResourceEditSection[]
}

interface IDetailsSectionsProps extends IProps {
  /**
   * Where to pin the expand/collapse-all toolbar: the height of the tab bar above
   * it, measured rather than hard-coded so the tab bar's padding and type scale
   * stay free to change. 0 when there is no tab bar.
   */
  stickyTopOffset?: number
}

// `data-vaul-no-drag` keeps a reorder drag from being read as a swipe-to-dismiss
// by the mobile vaul Drawer that hosts this card.
const DragHandle = SortableHandle(() => (
  <span
    data-vaul-no-drag=""
    onClick={(e: React.MouseEvent) => e.stopPropagation()}
    className="tw:flex tw:items-center tw:self-stretch tw:px-2 tw:text-muted-foreground tw:cursor-grab tw:active:cursor-grabbing"
  >
    <GripVerticalIcon size={16} />
  </span>
))

interface ISectionHeaderProps {
  label: string
  isExpanded: boolean
  onToggle: () => void
  withDragHandle: boolean
}

const SectionHeader: React.FC<ISectionHeaderProps> = ({
  label,
  isExpanded,
  onToggle,
  withDragHandle,
}) => {
  const Chevron = isExpanded ? ChevronDownIcon : ChevronRightIcon
  return (
    <div className="tw:flex tw:items-center tw:h-10 tw:bg-muted tw:border-0 tw:border-b tw:border-solid tw:border-border">
      {withDragHandle && <DragHandle />}
      <button
        type="button"
        aria-expanded={isExpanded}
        onClick={onToggle}
        className={`tw:flex tw:flex-1 tw:items-center tw:gap-2 tw:h-full tw:pr-3 tw:bg-transparent tw:border-0 tw:cursor-pointer tw:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-inset tw:focus-visible:ring-ring ${
          withDragHandle ? '' : 'tw:pl-3'
        }`}
      >
        <span className="tw:flex-1 tw:text-left tw:text-title-sm tw:text-muted-foreground">
          {label}
        </span>
        <Chevron size={16} className="tw:shrink-0 tw:text-muted-foreground" />
      </button>
    </div>
  )
}

interface ISectionBodyProps {
  isExpanded: boolean
  keepMounted?: boolean
  children: React.ReactNode
}

const SectionBody: React.FC<ISectionBodyProps> = ({
  isExpanded,
  keepMounted,
  children,
}) => {
  if (!isExpanded && !keepMounted) return null
  return (
    <div className={`tw:px-4 tw:pt-2 tw:pb-4 ${isExpanded ? '' : 'tw:hidden'}`}>
      {children}
    </div>
  )
}

interface ISectionViewProps extends ISectionHeaderProps {
  keepMounted?: boolean
  content: React.ReactNode
}

const SectionView: React.FC<ISectionViewProps> = ({
  content,
  keepMounted,
  ...header
}) => (
  <div className="tw:bg-background">
    <SectionHeader {...header} />
    <SectionBody isExpanded={header.isExpanded} keepMounted={keepMounted}>
      {content}
    </SectionBody>
  </div>
)

// Only the dragged clone needs a high z-index (applied via SortableContainer's
// helperClass). The static section wrappers must NOT be raised, or they'd paint
// over portaled overlays like FullScreenPickerSheet (the field pickers).
const SORTABLE_HELPER_CLASS = 'tw:z-[9999]'

const SortableSection = SortableElement(
  (props: Omit<ISectionViewProps, 'withDragHandle'>) => (
    <SectionView {...props} withDragHandle />
  ),
)

const SortableSectionList = SortableContainer(
  ({
    sections,
    expanded,
    onToggle,
  }: {
    sections: IResourceEditSection[]
    expanded: Set<string>
    onToggle: (id: string) => void
  }) => (
    // Top border closes the stack's top edge — the per-header bottom borders only
    // separate sections, leaving the first one without a line above it.
    <div className="tw:border-0 tw:border-t tw:border-solid tw:border-border">
      {sections.map((section, index) => (
        <SortableSection
          key={section.id}
          index={index}
          label={section.label}
          isExpanded={expanded.has(section.id)}
          onToggle={() => onToggle(section.id)}
          keepMounted={section.keepMounted}
          content={section.content}
        />
      ))}
    </div>
  ),
)

/**
 * The "Details" tab body: the resource's collapsible sections. A single section
 * (e.g. creation mode) renders plain and non-draggable; multiple sections are
 * drag-reorderable with an expand-all / collapse-all toggle above them.
 */
const DetailsSections: React.FC<IDetailsSectionsProps> = ({
  sections,
  stickyTopOffset = 0,
}) => {
  const {
    state: { isCreationMode },
  } = useResourceEdit()

  // The user's manual drag order (section ids); empty until they reorder. The
  // `sections` prop stays the source of truth (see orderedSections), so a
  // section added after mount is never dropped. Switching records resets this
  // via the recordId key on the Tabs root — no reset effect needed here.
  const [order, setOrder] = React.useState<string[]>([])
  const [expanded, setExpanded] = React.useState<Set<string>>(() => {
    const visibleAtMount = isCreationMode
      ? sections.filter(s => !s.requiresSavedRecord)
      : sections
    const flagged = visibleAtMount.filter(s => s.defaultExpanded)
    // Fall back to the first visible section when the chosen default isn't
    // visible (e.g. a requiresSavedRecord target in creation) or none is set,
    // so a card never opens with everything collapsed.
    const initial = flagged.length ? flagged : visibleAtMount.slice(0, 1)
    return new Set(initial.map(s => s.id))
  })

  // Seed expansion for sections that appear after mount (e.g. enabling StruxTag
  // mid-session adds Utilization/Sessions) so a newly-added default-expanded
  // section still opens.
  const seenIdsRef = React.useRef(new Set(sections.map(s => s.id)))
  React.useEffect(() => {
    const freshDefaultExpanded = sections.filter(
      s => !seenIdsRef.current.has(s.id) && s.defaultExpanded,
    )
    sections.forEach(s => seenIdsRef.current.add(s.id))
    if (freshDefaultExpanded.length) {
      setExpanded(prev => {
        const next = new Set(prev)
        freshDefaultExpanded.forEach(s => next.add(s.id))
        return next
      })
    }
  }, [sections])

  const toggleSection = React.useCallback((id: string) => {
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  // Apply the saved drag order to the sections that still exist, then append any
  // current sections missing from that order (added since the last reorder).
  const orderedSections = [
    ...order
      .map(id => sections.find(s => s.id === id))
      .filter((s): s is IResourceEditSection => !!s),
    ...sections.filter(s => !order.includes(s.id)),
  ]

  // Creation mode hides sections that need a saved record.
  const visibleSections = isCreationMode
    ? orderedSections.filter(s => !s.requiresSavedRecord)
    : orderedSections

  // A single visible section (the usual creation-mode case, but also any
  // one-section card) has nothing to reorder — render it plain and
  // non-draggable, without the expand/collapse-all toolbar.
  if (visibleSections.length <= 1) {
    return (
      <div className="tw:pt-3 tw:first:pt-0">
        {/* Top border closes the stack's top edge (see SortableSectionList). */}
        <div className="tw:border-0 tw:border-t tw:border-solid tw:border-border">
          {visibleSections.map(s => (
            <SectionView
              key={s.id}
              label={s.label}
              isExpanded={expanded.has(s.id)}
              onToggle={() => toggleSection(s.id)}
              withDragHandle={false}
              keepMounted={s.keepMounted}
              content={s.content}
            />
          ))}
        </div>
      </div>
    )
  }

  const hasExpandedSections = visibleSections.some(s => expanded.has(s.id))

  const toggleAll = () => {
    setExpanded(
      hasExpandedSections ? new Set() : new Set(visibleSections.map(s => s.id)),
    )
  }

  const handleSortEnd = ({
    oldIndex,
    newIndex,
  }: {
    oldIndex: number
    newIndex: number
  }) => {
    setOrder(
      arrayMove(
        visibleSections.map(s => s.id),
        oldIndex,
        newIndex,
      ),
    )
  }

  return (
    <div>
      {/* Pinned under the tab bar so the toggle stays reachable however far the
          sections scroll: opaque because they pass underneath it, and below the
          tab bar's tw:z-10 because DOM order would otherwise paint it on top. */}
      <div
        className="tw:sticky tw:z-[5] tw:flex tw:items-center tw:justify-start tw:bg-background tw:px-3 tw:pt-3 tw:pb-2"
        style={{ top: stickyTopOffset }}
      >
        <CollapseButton
          isExpanded={hasExpandedSections}
          onClick={toggleAll}
          ariaLabel={
            hasExpandedSections
              ? Localization.translator.collapseAll
              : Localization.translator.expandAll
          }
        />
      </div>
      <SortableSectionList
        sections={visibleSections}
        expanded={expanded}
        onToggle={toggleSection}
        onSortEnd={handleSortEnd}
        useDragHandle
        lockAxis="y"
        distance={5}
        helperClass={SORTABLE_HELPER_CLASS}
      />
    </div>
  )
}

/**
 * The resource card's properties area: a "Details" tab holding the collapsible
 * sections, plus a placeholder "Log" tab. The tab bar is pinned so it stays
 * visible while the sections scroll underneath.
 */
export const ResourceEditSections: React.FC<IProps> = ({ sections }) => {
  const {
    state: { recordId, isCreationMode },
  } = useResourceEdit()

  const [tabBarHeight, setTabBarHeight] = React.useState(0)

  // A callback ref rather than an effect: it runs in the commit that mounts the
  // bar, so the toolbar never paints a frame at top: 0 under it, and it runs again
  // if the bar mounts later — which is what happens when a card created in Maps
  // stays open and switches out of creation mode. The bar is fixed-height chrome,
  // so measuring once is enough; no ResizeObserver.
  const measureTabBar = React.useCallback((node: HTMLDivElement | null) => {
    if (node) setTabBarHeight(node.offsetHeight)
  }, [])

  // Creation shows only the properties form (a Log tab is meaningless with no
  // saved record yet), so skip the tab bar and render the collapsible section.
  if (isCreationMode) {
    return <DetailsSections sections={sections} />
  }

  return (
    // Key by record: the edit panel reuses this component across records rather
    // than remounting, so keying resets the active tab to Details (and the
    // section order / expanded state) when the selected record changes.
    <Tabs key={recordId} defaultValue="details" className="tw:gap-0">
      <div
        ref={measureTabBar}
        className="tw:sticky tw:top-0 tw:z-10 tw:bg-background tw:border-b tw:border-border tw:px-4"
      >
        <TabsList variant="line" className="tw:w-full">
          <TabsTrigger value="details">
            {Localization.translator.details}
          </TabsTrigger>
          <TabsTrigger value="log">{Localization.translator.log}</TabsTrigger>
        </TabsList>
      </div>

      {/* keepMounted: the form lives in the Details tab, so it must stay mounted
          (hidden) when the Log tab is active or the footer Save breaks. */}
      <TabsContent value="details" keepMounted>
        <DetailsSections sections={sections} stickyTopOffset={tabBarHeight} />
      </TabsContent>
      <TabsContent value="log">
        <div className="tw:px-4 tw:py-10 tw:text-center tw:text-body-md tw:text-muted-foreground">
          {Localization.translator.comingSoon}...
        </div>
      </TabsContent>
    </Tabs>
  )
}
